import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";
import PropTypes from "prop-types";
import { showErrorToast, showSuccessToast } from "~/components/Toast";
import { ADD_EXPENSE_SUCCESS } from "~/store/action_types/index.jsx";
import { addExpenseForTask } from "~/store/actions/actions.expenses.jsx";
import { getCurrencyCodeByProjectId } from "~/store/selectors/selector.currencies";
import { getActiveExpenseCategoriesArrayOfIdAndName } from "~/store/selectors/selector.expenseCategories";
import { getDecimalAndThousandSeparators } from "~/store/selectors/selector.languages";
import { getCanLoggedUserChangeBillableStatus } from "~/store/selectors/selector.tracking.jsx";
import { ExpenseForm } from "./ExpenseForm";

export const ExpenseFormContainer = ({
  projectId,
  recordDate,
  parentId,
  parentType,
  taskId,
  userId,
  onDismiss,
  displaySuccessToast,
  ...rest
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [submitInProgress, setSubmitInProgress] = useState(false);

  const canSeeBillable = useSelector((state) => getCanLoggedUserChangeBillableStatus(state, { projectId }));

  const currencyCode = useSelector((state) => getCurrencyCodeByProjectId(state, projectId));

  const expenseCategories = useSelector(getActiveExpenseCategoriesArrayOfIdAndName);

  const { decimal_separator, thousand_separator } = useSelector(getDecimalAndThousandSeparators);

  const handleSubmit = useCallback(
    (form) => {
      setSubmitInProgress(true);
      let date = recordDate;

      if (typeof recordDate === "number") {
        date = moment.unix(recordDate).format("YYYY-MM-DD");
      }

      const attributes = {
        user_id: userId,
        record_date: date,
        parent_id: parentId,
        parent_type: parentType,
        ...form,
      };

      if (taskId && attributes) {
        attributes.task_id = taskId;
      }

      return dispatch(addExpenseForTask(projectId, attributes)).then((action) => {
        if (action && action.error) {
          showErrorToast(t("Failed to save expense"));
          setSubmitInProgress(false);
          return;
        } else if (action.type === ADD_EXPENSE_SUCCESS) {
          displaySuccessToast && showSuccessToast(t("Expense added"));
        }
        onDismiss(action);
      });
    },
    [recordDate, userId, parentId, parentType, taskId, dispatch, projectId, onDismiss, t, displaySuccessToast]
  );

  return (
    <ExpenseForm
      {...rest}
      expenseCategories={expenseCategories}
      canSeeBillable={canSeeBillable}
      onSubmit={handleSubmit}
      isSubmitInProgress={submitInProgress}
      currencyCode={currencyCode}
      decimalSeparator={decimal_separator}
      thousandSeparator={thousand_separator}
    />
  );
};

ExpenseFormContainer.propTypes = {
  projectId: PropTypes.number.isRequired,
  recordDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  parentId: PropTypes.number,
  parentType: PropTypes.string,
  taskId: PropTypes.number,
  userId: PropTypes.number,
  onDismiss: PropTypes.func,
  displaySuccessToast: PropTypes.bool,
};

ExpenseFormContainer.defaultProps = {
  displaySuccessToast: true,
};
